const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/StartPage/StartPage.tsx';
import { css, cx } from '@emotion/css';
import * as Styles from './styles';
import { logoFrameStyle } from './styles';
import { AppBar } from 'App/components/AppBar/AppBar';
import { ImageInplaceLoader } from 'App/components/ImageClientLoader/ImageClientLoader';
import { getPatAppSettings, hasNativeWrapper } from 'App/utils/generalUtils';
import { theme } from 'App/utils/clientSetupManager';
import useBreakpoint from '@cistec/common_web/hooks/useBreakpoint';
import { BigPicture } from 'App/components/BigPicture/BigPicture';
import { Box, Button, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import textMap from './translations';
import { GapBig } from 'App/components/Gap/Gap';
import { Footer } from 'App/components/Footer/Footer';
import { logoInlineStyle, logoStyleSmallDevice } from '../LoginPage/styles';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import LoadingSpinner from '@cistec/common_web/components/LoadingSpinner';
import { openExternalBrowser } from '@cistec/common_web/actions/nativeActions';
import { securedLoginUrl } from 'App/utils/urlLocationCheck';
import { getConfiguration } from 'App/utils/clientSetupGetRessource';
import { LoginSteps } from '../LoginPage/LoginSteps';
import { RouteComponentProps } from 'react-router';
import { LoginButton } from './LoginButton/LoginButton';
import { appAuthIsLoading } from 'App/store/nativeAppAuth';
import { useTheme } from '@mui/material/styles';
import { PATH_DASHBOARD } from 'App/defs/router';
import { useFeatureFlags } from 'App/store/featureFlags';
import { ACTION_TYPE_NATIVE_PATAPP_AUTHORIZE_PENDING } from 'App/actions/patAppNative';
import { MetaKatAlertList } from 'App/components/Alert/MetaKatAlertList';
import { invitationCode as invitationCodeSelector } from 'App/store/ui';

// Router arguments
export interface IRouteParams {
    prodId: string;
}

interface IProps {}

export type StartButtonType = 'aufgebot' | 'login' | undefined;
export type LoginStage = 'invitation-code' | 'sms-code';

const cssBtnWrapper = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const cssBoxStyle = css`
    height: 180px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    & .MuiPaper-root {
        width: 160px;
    }
`;

const cssIamSection = css`
    display: flex;
    flex-direction: column;
    flex: 1;
    row-gap: ${theme.spacing(5)};
    margin-top: ${theme.spacing(5)};
    width: 100%;
`;

export const StartPage = (props: IProps & RouteComponentProps<IRouteParams>) => {
    const { formatMessage } = useIntl();
    const settings = getPatAppSettings();
    const theme = useTheme();
    const isSmallDevice = useBreakpoint(theme?.breakpoints?.values?.sm);
    const isMediumDevice = useBreakpoint(theme?.breakpoints?.values?.md);
    const invitationCode = useSelector(invitationCodeSelector);
    const { welcomeText, welcomeTitle, navbarTitle } = getConfiguration();

    const logo = <ImageInplaceLoader className={isSmallDevice ? logoStyleSmallDevice : logoInlineStyle} src={'Logo'} />;
    const nativeAppAuthIsLoading = useSelector(appAuthIsLoading);
    const dispatch = useDispatch<any>();

    const { data: featureFlags } = useFeatureFlags();
    const [selectedButton, setSelectedButton] = useState<StartButtonType>(undefined);

    useEffect(() => {
        if (!featureFlags.login.allowIAM || invitationCode) {
            setSelectedButton('aufgebot');
        }
    }, []);

    const getRegistrationSection = () => {
        const registerUrl = `${window.location.origin}/register?loginurl=${window.location.origin}/secured/login`;
        const handleRegisterButtonClick = () => {
            if (hasNativeWrapper) {
                dispatch(openExternalBrowser(registerUrl, 'Login', false));
            } else {
                document.location.href = registerUrl;
            }
        };

        return (
            <div className={cssBtnWrapper}>
                <Typography variant={'body1'}>{formatMessage(textMap.register)}</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    className={Styles.btnRoot}
                    onClick={handleRegisterButtonClick}
                >
                    {formatMessage(textMap.regsitration)}
                </Button>
            </div>
        );
    };
    const handleIamLoginButtonClick = () => {
        if (hasNativeWrapper) {
            dispatch({ type: ACTION_TYPE_NATIVE_PATAPP_AUTHORIZE_PENDING });
        } else {
            document.location.href = `/${securedLoginUrl}/#${PATH_DASHBOARD}/`;
        }
    };
    const iamSection = (
        <div className={cssIamSection}>
            <div className={cssBtnWrapper}>
                <Typography variant={'body1'}>{formatMessage(textMap.loginText)}</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    className={Styles.btnRoot}
                    onClick={handleIamLoginButtonClick}
                >
                    {formatMessage(textMap.loginBtnLabel)}
                </Button>
            </div>

            {featureFlags.login.allowRegristration && getRegistrationSection()}
        </div>
    );

    return (
        <>
            <AppBar
                primaryText={navbarTitle ? navbarTitle : settings.hospitalLongName}
                logoElement={logo}
                showNotificationBtn={false}
                bgndColor={theme.palette.background.paper}
                textColor={theme.palette.primary.main}
                className={cx(logoFrameStyle, isSmallDevice ? Styles.logoFrameStyleVerticalCenter : undefined)}
            />

            <div className={cx(Styles.frameStyle)}>
                {!!nativeAppAuthIsLoading && <LoadingSpinner center="absolute" />}
                <div className={isSmallDevice ? Styles.frameStyleAbove : Styles.frameStyleLeft}>
                    <BigPicture isVisible={!isSmallDevice} />
                </div>

                <div className={isSmallDevice ? Styles.frameStyleBelow : Styles.frameStyleRight}>
                    <div className={isSmallDevice ? Styles.contentWrapperSmDevices : Styles.contentWrapper}>
                        <Typography variant={'h2'} component="h1" className={Styles.startPageStyleTitle}>
                            {welcomeTitle ? formatMessage(welcomeTitle) : formatMessage(textMap.startTitle)}
                        </Typography>
                        {!isSmallDevice && <GapBig />}
                        <MetaKatAlertList />
                        <p
                            className={Styles.welcomeMessage}
                            dangerouslySetInnerHTML={{
                                __html: welcomeText
                                    ? formatMessage(welcomeText)
                                    : formatMessage(textMap.welcomeText)
                                          // need to use .replace() due to adding html tags the formatMessage replacement functionality is not working anymore
                                          .replace('{hospitalName}', settings.hospitalLongName),
                            }}
                        />
                        {!isSmallDevice && <GapBig />}

                        <Box className={cssBoxStyle}>
                            {!nativeAppAuthIsLoading && (
                                <LoginButton
                                    isSelected={selectedButton === 'aufgebot'}
                                    className={cx({ dimmed: selectedButton === 'login' })}
                                    onClick={() => {
                                        setSelectedButton('aufgebot');
                                    }}
                                    text={formatMessage(textMap.withAufgebot)}
                                    iconName={'icons8-patapp-letter'}
                                />
                            )}
                            {featureFlags.login.allowIAM && (
                                <LoginButton
                                    isSelected={selectedButton === 'login'}
                                    className={cx({ dimmed: selectedButton === 'aufgebot' })}
                                    onClick={() => {
                                        setSelectedButton('login');
                                    }}
                                    text={formatMessage(textMap.withLogin)}
                                    iconName={'icons8-patapp-login'}
                                />
                            )}
                        </Box>

                        {selectedButton === 'aufgebot' && <LoginSteps prefillInvitationCode={invitationCode} />}

                        {selectedButton === 'login' && <>{iamSection}</>}
                    </div>
                </div>
            </div>
            {(isSmallDevice || isMediumDevice) && <Footer />}
        </>
    );
};
